import { NotificationChannel, NotificationType } from "@prisma/client"

export const CONFIG = {
    MAX_DURATION_EVENT: 24 * 60,
    MAX_DURATION_DEADLINE_EVENT: 100 * 60,
    PASSWORD_MIN_LENGTH: 12,
    MEDIA_QUERY_BREAK: "(min-width: 768px)",
    DEFAULT_TABLE_SIZE: 100,
    GROUPS_PAGE_SIZE: 500,
    GROUP_ITEMS_PAGE_SIZE: 24,
    /** Template body default — matches {@link fontsList} `name` (`segoe` = host Segoe UI stack). */
    DEFAULT_FONT: "segoe",
    /** PDF / Lexical body default in **pt**. Sheet `1em` = this × (96/72) CSS px (e.g. 9pt → 12px). */
    DEFAULT_FONTSIZE: 9,
    CALENDAR_HOUR_START: 8,
    CALENDAR_HOUR_END: 20,
    TABLE_MENU_BUTTON_WIDTH: 50,
    HOVER_CARD_HOVER_DELAY: 300, // the delay the app will wait before sending the request to the server. Avoid flooding the server with requests
    /**
     * ms after successful submit before details panel URL swap (when slug changes), `lockForm()`, and
     * `requestState` inactive — keeps success button / CheckSVG animation visible. Tune with `CheckSVG` timing (~800ms).
     */
    FORM_SUCCESS_FEEDBACK_SETTLE_MS: 850,
    DEFAULT_TICKETS_NOTIFICATIONS: [
        {
            type: NotificationType.CLOSED,
            channels: [NotificationChannel.IN_APP],
        },
        {
            type: NotificationType.MENTIONNED_THIS_TICKET,
            channels: [],
        },
        {
            type: NotificationType.MERGED,
            channels: [NotificationChannel.IN_APP],
        },
        {
            type: NotificationType.NEW_COMMENT_ALL,
            channels: [],
        },
        {
            type: NotificationType.NEW_COMMENT_FROM_SUPPORT_OR_DEV,
            channels: [NotificationChannel.IN_APP],
        },
        {
            type: NotificationType.NEW_OCCURENCE,
            channels: [],
        },
        {
            type: NotificationType.NEW_TICKET,
            channels: [],
        },
        {
            type: NotificationType.OCCURENCE_REFUSED,
            channels: [],
        },
        {
            type: NotificationType.OCCURENCE_VALIDATED,
            channels: [],
        },
        {
            type: NotificationType.REOPENED,
            channels: [],
        },
        {
            type: NotificationType.RESOLVED,
            channels: [NotificationChannel.IN_APP],
        },
        {
            type: NotificationType.VALIDATED,
            channels: [],
        },
    ],
    METADATA_NO_SESSION: {
        title: "Erreur - Session invalide",
        description: "Une connexion est requise pour accéder à cette page.",
    },
    METADATA_NOT_FOUND: {
        title: "Erreur - Page introuvable",
        description: "La page demandée n'existe pas.",
    },
}

export enum TableId {
    events = "events",
    cdcs = "cdcs",
    contracts = "contracts",
    teachers = "teachers",
    tickets = "tickets",
    //
    administration_users = "administration_users",
    administration_administration_roles = "administration_administration_roles",
    administration_site_roles = "administration_site_roles",
    administration_faculty_roles = "administration_faculty_roles",
    administration_teacher_statuses = "administration_teacher_statuses",
    administration_teacher_skills = "administration_teacher_skills",
    administration_skills = "administration_skills",
    administration_teacher_sites = "administration_teacher_sites",
    administration_event_types = "administration_event_types",
    administration_sites = "administration_sites",
    cdc_templates = "cdc_templates",
    contract_templates = "contract_templates",
    //
    site_users = "site_users",
    /** Cross-scope (site + administration settings); shared user preferences. */
    event_locations = "event_locations",
    rooms = "rooms",
    site_faculties = "site_faculties",
    site_fees = "site_fees",
    site_process = "site_process",
    site_direction = "site_direction",
    //
    faculty_users = "faculty_users",
    faculty_promotions = "faculty_promotions",
    faculty_learning_unit_trees = "faculty_learning_unit_trees",
    faculty_learning_unit_levels = "faculty_learning_unit_levels",
    faculty_learning_units = "faculty_learning_units",
    faculty_semesters = "faculty_semesters",
}

/**
 * React Query key scope for SearchViewWithQuery (main search + grouped table page queries).
 * Invalidation must use `[SEARCH_VIEW_QUERY_KEY_SCOPE, tableId, ...]` so it does not collide with
 * unrelated queries that start with the same `TableId` (e.g. get-details: `[tableId, slug, apiRoute]`).
 */
export const SEARCH_VIEW_QUERY_KEY_SCOPE = "svq" as const;

export const teacherInformationsToRedact = [
    "socialSecurityNumber",
    "birthName",
    "birthPlace",
    "adress_streetNumber",
    "adress_bis_ter",
    "adress_streetName",
    "adress_streetType",
    "adress_other",
    "adress_postalCode",
    "adress_city",
    "comment",
    "key",
    "siret",
    "matricule",
    "nationality",
    "endOfValidity",
    "qualiopiCertificationNumber",
    "activityDeclarationNumber",
    "organizationLegalStatus",
    "structureLegalRepresentative",
]

export const LEARNING_UNITS_VIEW_MODES = {
    PER_SEMESTER: "per-semester",
    PER_TREE: "per-tree",
} as const;

export const LEARNING_UNITS_TAB_PREFERENCE_KEY = "LEARNING_UNITS_TABS";